import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import axiosInstance from '../../helpers/axios';

const ACCEPTED_EXTENSIONS = '.xlsx,.xls,.csv';

function statusChip(row) {
    if (row.status === 'valid') return <Chip size="small" color="success" label="✓ Valid" />;
    if (row.status === 'skipped') return <Chip size="small" color="warning" label={`⚠ Skipped (${row.reason})`} />;
    return <Chip size="small" color="error" label={`✗ Error (${row.reason})`} />;
}

export default function ImportAssignmentModal({ open, onClose, onSuccess }) {
    const fileInputRef = useRef(null);
    const [step, setStep] = useState('upload');
    const [isDragging, setIsDragging] = useState(false);

    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const [preview, setPreview] = useState([]);
    const [summary, setSummary] = useState({ valid: 0, skipped: 0, errors: 0 });

    const [isConfirming, setIsConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState(null);
    const [confirmResult, setConfirmResult] = useState(null);

    const resetAll = () => {
        setStep('upload');
        setIsDragging(false);
        setIsUploading(false);
        setUploadError(null);
        setPreview([]);
        setSummary({ valid: 0, skipped: 0, errors: 0 });
        setIsConfirming(false);
        setConfirmError(null);
        setConfirmResult(null);
    };

    useEffect(() => {
        if (open) resetAll();
    }, [open]);

    const handleFile = (file) => {
        if (!file) return;
        setIsUploading(true);
        setUploadError(null);

        const formData = new FormData();
        formData.append('file', file);

        axiosInstance
            .post('/admin-settings/loan-hypothecation/import-preview', formData)
            .then((response) => {
                setPreview(response.data.data.preview);
                setSummary(response.data.data.summary);
                setStep('preview');
            })
            .catch((error) => {
                setUploadError(error?.response?.data?.message || 'Could not process the uploaded file.');
            })
            .finally(() => setIsUploading(false));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFile(e.dataTransfer.files?.[0]);
    };

    const handleConfirm = () => {
        setIsConfirming(true);
        setConfirmError(null);
        setConfirmResult(null);

        const assignments = preview
            .filter((row) => row.status === 'valid')
            .map((row) => ({ loanId: row.loanId, fundId: row.fundId }));

        axiosInstance
            .post('/admin-settings/loan-hypothecation/import-confirm', { assignments })
            .then((response) => {
                const { success, failed, errors } = response.data.data;
                if (failed > 0) {
                    setConfirmResult({ success, failed, errors });
                    onSuccess();
                } else {
                    onSuccess(`Successfully assigned ${success} loans`);
                    onClose();
                }
            })
            .catch((error) => {
                setConfirmError(error?.response?.data?.message || 'Could not process the import.');
            })
            .finally(() => setIsConfirming(false));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>📤 Import Loan-Fund Assignments</DialogTitle>
            <DialogContent>
                {step === 'upload' && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '20px' }}>
                            Upload the filled-in free-loans Excel file to assign loans to funds in bulk.
                        </Typography>

                        {uploadError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{uploadError}</Alert>}

                        <Box
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragging ? '#3b82f6' : '#cbd5e1',
                                borderRadius: '12px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: isDragging ? '#f8fafc' : 'transparent',
                            }}
                        >
                            <Typography sx={{ fontSize: '36px', marginBottom: '8px' }}>📁</Typography>
                            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                                Drag &amp; drop your Excel file here, or <strong>click to browse</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                                Accepts .xlsx, .xls, .csv files
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_EXTENSIONS}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                        </Box>

                        {isUploading && <LinearProgress sx={{ marginTop: '16px' }} />}
                    </>
                )}

                {(step === 'preview') && (
                    <>
                        {confirmError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{confirmError}</Alert>}
                        {confirmResult && (
                            <Alert severity="warning" sx={{ marginBottom: '16px' }}>
                                Assigned {confirmResult.success}, failed {confirmResult.failed}
                                {confirmResult.errors?.length > 0 && ` (${confirmResult.errors.map((e) => e.reason).join('; ')})`}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                            <Chip color="success" label={`✓ ${summary.valid} Valid`} />
                            <Chip color="warning" label={`⚠ ${summary.skipped} Skipped`} />
                            <Chip color="error" label={`✗ ${summary.errors} Errors`} />
                        </Box>

                        <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Loan ID</TableCell>
                                        <TableCell>Fund ID</TableCell>
                                        <TableCell>Fund Name</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {preview.map((row, i) => (
                                        <TableRow key={`${row.loanId}-${i}`}>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{row.loanId}</TableCell>
                                            <TableCell>{row.fundId}</TableCell>
                                            <TableCell>{row.fundName || '—'}</TableCell>
                                            <TableCell>{statusChip(row)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isUploading || isConfirming}>Cancel</Button>
                {step === 'preview' && (
                    <Button onClick={resetAll} disabled={isConfirming}>Start Over</Button>
                )}
                {step === 'preview' && (
                    <Button variant="contained" onClick={handleConfirm} disabled={summary.valid === 0 || isConfirming}>
                        {isConfirming ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : `Process ${summary.valid} Valid Assignments`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
